/**
 * Privacy API Client
 *
 * PIPEDA-compliant API functions for user data management:
 * - Right to Access (view data)
 * - Right to Data Portability (export data)
 * - Right to Erasure (delete account)
 * - Right to Withdraw Consent (cookie preferences)
 */

import { buildApiUrl, handleApiResponse } from '@shared/api';

/**
 * Cookie preferences structure
 */
export interface CookiePreferences {
  essential: boolean; // Always true (cannot be disabled)
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

/**
 * User data export response
 */
export interface ExportUserDataResponse {
  exportDate: string;
  userData: {
    id: string;
    email: string;
    isVerified: boolean;
    systemRoles: string[];
    createdAt: string;
    updatedAt: string;
    lastLoginAt: string | null;
    cookiePreferences: CookiePreferences | null;
    // PIPEDA consent tracking
    termsAcceptedAt: string | null;
    termsVersion: string | null;
    privacyAcceptedAt: string | null;
    privacyVersion: string | null;
    marketingConsent: boolean;
    marketingConsentAt: string | null;
    ageVerified: boolean;
    ageVerifiedAt: string | null;
    // Actors will be added in future
  };
  notice: string;
}

/**
 * Update cookie preferences request
 */
export interface UpdateCookiePreferencesRequest {
  preferences: CookiePreferences;
}

/**
 * Update cookie preferences response
 */
export interface UpdateCookiePreferencesResponse {
  message: string;
  preferences: CookiePreferences;
}

/**
 * Delete user account
 * PIPEDA: Right to Erasure
 *
 * - Soft deletes account (anonymizes data)
 * - Keeps financial records for compliance
 * - Irreversible action
 * - Rate limited: 2 requests per day
 *
 * @returns Promise<void> - 204 No Content on success
 * @throws ApiError if request fails
 */
export async function deleteAccount(): Promise<void> {
  const url = buildApiUrl('/users/me');
  console.log('[privacy.api] deleteAccount - URL:', url);

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      credentials: 'include', // Include cookies for authentication
    });

    console.log('[privacy.api] deleteAccount - Response status:', response.status);

    await handleApiResponse<void>(response);
    console.log('[privacy.api] deleteAccount - Success: Account deleted');
  } catch (error) {
    console.error('[privacy.api] deleteAccount - Error:', error);
    throw error;
  }
}

/**
 * Export all user data
 * PIPEDA: Right to Data Portability
 *
 * Returns all personal data stored in the system as JSON
 * Rate limited: 3 requests per hour
 *
 * @returns Promise<ExportUserDataResponse> - All user data in JSON format
 * @throws ApiError if request fails
 */
export async function exportUserData(): Promise<ExportUserDataResponse> {
  const url = buildApiUrl('/users/me/export');
  console.log('[privacy.api] exportUserData - URL:', url);

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include', // Include cookies for authentication
    });

    console.log('[privacy.api] exportUserData - Response status:', response.status);

    const result = await handleApiResponse<ExportUserDataResponse>(response);
    console.log('[privacy.api] exportUserData - Success: Data exported');
    return result;
  } catch (error) {
    console.error('[privacy.api] exportUserData - Error:', error);
    throw error;
  }
}

/**
 * Update cookie consent preferences
 * PIPEDA: Right to Withdraw Consent
 *
 * - Essential cookies cannot be disabled (always true)
 * - Rate limited: 10 requests per minute
 *
 * @param preferences - Cookie preferences object
 * @returns Promise<UpdateCookiePreferencesResponse> - Updated preferences
 * @throws ApiError if request fails
 */
export async function updateCookiePreferences(
  preferences: CookiePreferences
): Promise<UpdateCookiePreferencesResponse> {
  const url = buildApiUrl('/users/me/cookie-preferences');
  console.log('[privacy.api] updateCookiePreferences - URL:', url);
  console.log('[privacy.api] updateCookiePreferences - Preferences:', preferences);

  // Ensure essential cookies are always enabled
  const sanitizedPreferences: CookiePreferences = {
    ...preferences,
    essential: true, // Force essential to true
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({ preferences: sanitizedPreferences }),
    });

    console.log('[privacy.api] updateCookiePreferences - Response status:', response.status);

    const result = await handleApiResponse<UpdateCookiePreferencesResponse>(response);
    console.log('[privacy.api] updateCookiePreferences - Success:', result);
    return result;
  } catch (error) {
    console.error('[privacy.api] updateCookiePreferences - Error:', error);
    throw error;
  }
}

/**
 * Download user data as JSON file
 * Helper function to trigger browser download
 *
 * @param data - User data export response
 * @param filename - Optional filename (default: 'my-data-YYYY-MM-DD.json')
 */
export function downloadUserDataAsJson(
  data: ExportUserDataResponse,
  filename?: string
): void {
  const date = new Date().toISOString().split('T')[0];
  const defaultFilename = `hummii-data-export-${date}.json`;
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || defaultFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
